import { Injectable } from '@angular/core';
// Reuse the EXISTING frame-accurate decoder used by the background render worker.
// mediabunny + WebCodecs run on the main thread too, so the same DecodedSource that
// streams frames for the renderer is what we use here to replace the laggy
// <video>.currentTime-seek preview path. (worker-decode has a `/// <reference
// lib="webworker" />` directive, but that only widens THAT file's lib set during
// compilation — importing its exported class here is fine under tsconfig.app.json,
// which compiles src/** as one unit.)
import { DecodedSource, type DecodedVisual } from '../../../core/render-runtime/worker/worker-decode';

/** A drawable video frame for the canvas compositor, plus a release hook. */
export interface PreviewDrawable {
  /** Intrinsic pixel size of the decoded frame. */
  width: number;
  height: number;
  /** Blit the frame, scaling into the destination rect (matches VideoSample.draw / drawImage). */
  draw: (ctx: CanvasRenderingContext2D, dx: number, dy: number, dw: number, dh: number) => void;
  /** Release the frame. Reader-owned frames are a no-op; the getSample fallback closes here. */
  close: () => void;
}

/** Sentinel returned when this source cannot be decoded by mediabunny — caller uses the <video> path. */
export const MEDIABUNNY_FALLBACK = Symbol('mediabunny-fallback');
export type PreviewDrawableResult = PreviewDrawable | typeof MEDIABUNNY_FALLBACK | null;

/** Isolated decoder used by batch tools so preview playback cannot contend with analysis seeks. */
export interface MediabunnyFrameSession {
  getDrawableAt(sourceTimeSeconds: number): Promise<PreviewDrawable | null>;
  dispose(): void;
}

/** One cached snapshot: an OffscreenCanvas holding a previously-decoded frame at its native size. */
interface CachedSnapshot {
  canvas: OffscreenCanvas;
  width: number;
  height: number;
}

/** Per-source state: the decoder, its build promise, and a tiny snapshot LRU. */
interface SourceEntry {
  /** Resolves to the DecodedSource, or null if the build failed (→ permanent fallback). */
  ready: Promise<DecodedSource | null>;
  decoded: DecodedSource | null;
  failed: boolean;
  /** round(sourceTime*FPS) → snapshot, so wiggling the playhead re-serves instantly. */
  lru: Map<number, CachedSnapshot>;
}

/** How finely the LRU keys source time. 30 keys/sec is plenty for scrub re-serve. */
const LRU_FPS = 30;
/** Small bounded cache per source. */
const LRU_MAX = 14;

/**
 * Frame-accurate preview frame source backed by mediabunny (the same decoder the
 * render worker uses). Replaces the off-DOM `<video>` whose `currentTime` the
 * preview used to seek per frame — that seek forced a re-decode from the nearest
 * keyframe each time, which is what made dragging the playhead laggy.
 *
 * Frame lifecycle mirrors worker-decode's discipline:
 *   - The streaming fast path returns a reader-owned frame whose close() is a
 *     no-op (the SequentialFrameReader reuses + closes it on advance/dispose).
 *   - The getSample fallback inside DecodedSource returns a frame the caller owns;
 *     its close() really closes it. We forward that close() faithfully.
 *   - The LRU caches a CHEAP, independent snapshot (the frame drawn once onto a
 *     small OffscreenCanvas via the public draw API), so caching never touches the
 *     decoder's VideoFrame lifecycle and there are no GPU VideoFrame leaks.
 */
@Injectable({ providedIn: 'root' })
export class MediabunnyFrameSourceService {
  private readonly sources = new Map<string, SourceEntry>();

  /**
   * Returns a drawable for `url` at the given SOURCE time (seconds), or
   * MEDIABUNNY_FALLBACK if this source can't be decoded (caller draws via <video>),
   * or null if no frame is available yet (caller can keep its current pixels).
   */
  async getDrawableAt(url: string, sourceTimeSeconds: number, blob?: Blob | null): Promise<PreviewDrawableResult> {
    const entry = this.ensureEntry(url, blob);
    if (entry.failed) return MEDIABUNNY_FALLBACK;

    const decoded = entry.decoded ?? (await entry.ready);
    if (!decoded || entry.failed) return MEDIABUNNY_FALLBACK;
    if (!decoded.hasVideo()) return MEDIABUNNY_FALLBACK;

    const t = Math.max(0, sourceTimeSeconds);
    const key = Math.round(t * LRU_FPS);

    // 1) LRU re-serve — wiggling the playhead around a recently-seen time is free.
    const cached = entry.lru.get(key);
    if (cached) {
      this.touchLru(entry.lru, key, cached);
      return this.drawableFromSnapshot(cached);
    }

    // 2) Decode (streams forward; only a real backward jump re-seeks).
    let visual;
    try {
      visual = await decoded.getDrawable(t);
    } catch {
      // A single failed decode shouldn't kill the source permanently — fall back
      // for this frame only (the decoder may recover on the next request).
      return MEDIABUNNY_FALLBACK;
    }
    if (!visual) return null;

    // Snapshot into the LRU using ONLY the public draw API (no reach into the
    // decoder's VideoFrame). Cheap (one GPU blit) and fully owned by us.
    const snapshot = this.snapshot(visual.frame);
    if (snapshot) this.putLru(entry.lru, key, snapshot);

    return {
      width: visual.frame.width,
      height: visual.frame.height,
      draw: (ctx, dx, dy, dw, dh) =>
        // VideoSample.draw accepts CanvasRenderingContext2D|OffscreenCanvasRenderingContext2D;
        // DrawableFrame narrows the param to OffscreenCanvas for the worker, so cast at this seam.
        visual!.frame.draw(ctx as unknown as OffscreenCanvasRenderingContext2D, dx, dy, dw, dh),
      // Forward the reader's close() — no-op for reader-owned frames, real close for
      // the getSample fallback path inside DecodedSource.
      close: () => visual!.close(),
    };
  }

  /**
   * Like {@link getDrawableAt}, but returns a CALLER-OWNED frame that does NOT touch
   * the shared streaming reader. Use this for the SECOND-and-later active clip that
   * shares a source URL at the same preview frame (a duplicated clip over the
   * original, a same-source PiP/overlay video): the streaming reader can only hold one
   * sample at a time, so advancing it for clip #2 would close clip #1's frame and
   * corrupt the first draw. This path does a one-off `getSample` seek (via
   * {@link DecodedSource.getOwnedDrawable}) whose `close()` really frees the sample —
   * mirrors the render worker's `getOwnedDrawable` discipline on the preview side.
   *
   * Images reuse the shared bitmap (no per-frame sample to own), so an image used by
   * two clips is safe either way. The LRU is read-only-safe here (a snapshot is an
   * independent OffscreenCanvas, not the decoder's VideoFrame), so a recently-seen
   * time still re-serves for free without disturbing the streaming reader.
   */
  async getOwnedDrawableAt(url: string, sourceTimeSeconds: number, blob?: Blob | null): Promise<PreviewDrawableResult> {
    const entry = this.ensureEntry(url, blob);
    if (entry.failed) return MEDIABUNNY_FALLBACK;

    const decoded = entry.decoded ?? (await entry.ready);
    if (!decoded || entry.failed) return MEDIABUNNY_FALLBACK;
    if (!decoded.hasVideo()) return MEDIABUNNY_FALLBACK;

    const t = Math.max(0, sourceTimeSeconds);
    const key = Math.round(t * LRU_FPS);

    // 1) LRU re-serve — independent snapshot, safe and doesn't move the reader.
    const cached = entry.lru.get(key);
    if (cached) {
      this.touchLru(entry.lru, key, cached);
      return this.drawableFromSnapshot(cached);
    }

    // 2) Owned seek — does NOT use the streaming reader (so clip #1's streamed frame
    //    this same preview frame stays valid). Caller MUST close the returned frame.
    let visual;
    try {
      visual = await decoded.getOwnedDrawable(t);
    } catch {
      return MEDIABUNNY_FALLBACK;
    }
    if (!visual) return null;

    // Cache an independent snapshot so the next request at this time is free. The
    // snapshot copy happens BEFORE the caller closes the owned sample (caller closes
    // it after drawing), so the blit reads a live frame.
    const snapshot = this.snapshot(visual.frame);
    if (snapshot) this.putLru(entry.lru, key, snapshot);

    return {
      width: visual.frame.width,
      height: visual.frame.height,
      draw: (ctx, dx, dy, dw, dh) =>
        visual!.frame.draw(ctx as unknown as OffscreenCanvasRenderingContext2D, dx, dy, dw, dh),
      // Caller-owned: this really closes the seeked sample (no reader sharing it).
      close: () => visual!.close(),
    };
  }

  /** Pre-build the decoder for a source so the first preview frame is instant. Never throws. */
  async prepare(url: string, blob?: Blob | null): Promise<void> {
    try {
      await this.ensureEntry(url, blob).ready;
    } catch {
      /* failure already recorded on the entry → fallback */
    }
  }

  /**
   * Open an isolated instance of the existing Mediabunny decoder for sequential
   * batch analysis. The shared preview decoder keeps its own reader and LRU; an
   * Auto-Reframe seek therefore cannot block or advance the frame used on canvas.
   */
  async openDedicatedSession(url: string, blob?: Blob | null): Promise<MediabunnyFrameSession | null> {
    let sourceBlob = blob ?? null;
    try {
      if (!sourceBlob && url.toLowerCase().startsWith('blob:')) {
        const response = await fetch(url);
        if (!response.ok) return null;
        sourceBlob = await response.blob();
      }
      const decoded = await DecodedSource.create({
        id: `analysis:${url}`,
        kind: 'video',
        blob: sourceBlob,
        url: sourceBlob ? null : url,
      });
      if (!decoded.hasVideo()) {
        decoded.dispose();
        return null;
      }
      return {
        getDrawableAt: async sourceTimeSeconds => {
          const visual = await decoded.getDrawable(Math.max(0, sourceTimeSeconds));
          return visual ? this.drawableFromDecodedVisual(visual) : null;
        },
        dispose: () => decoded.dispose(),
      };
    } catch {
      return null;
    }
  }

  /** True once the decoder for this source has a usable video track. */
  isReady(url: string): boolean {
    const entry = this.sources.get(url);
    return !!entry && !entry.failed && !!entry.decoded && entry.decoded.hasVideo();
  }

  /** True if this source is known-undecodable (caller should use the <video> path). */
  hasFailed(url: string): boolean {
    return !!this.sources.get(url)?.failed;
  }

  /** Release one source's decoder + cached snapshots. */
  dispose(url: string): void {
    const entry = this.sources.get(url);
    if (!entry) return;
    entry.lru.clear();
    entry.decoded?.dispose();
    this.sources.delete(url);
  }

  /** Release everything (editor teardown). */
  disposeAll(): void {
    for (const url of [...this.sources.keys()]) this.dispose(url);
  }

  // ── internals ───────────────────────────────────────────────────────────

  private ensureEntry(url: string, blob?: Blob | null): SourceEntry {
    const existing = this.sources.get(url);
    if (existing) return existing;

    const entry: SourceEntry = { ready: Promise.resolve(null), decoded: null, failed: false, lru: new Map() };
    entry.ready = (async () => {
      try {
        // Reuse DecodedSource.create — the exact builder the worker uses. We feed it
        // a minimal RenderSource ({id,kind,blob|url}); it probes real dimensions/duration.
        // Object URLs already point at an in-memory Blob. Resolve them once and give Mediabunny
        // a BlobSource so random-access frame decoding does not create a new browser media-range
        // request for every ruler movement. Remote URLs remain UrlSource-backed and streamable.
        let sourceBlob = blob ?? null;
        if (!sourceBlob && url.toLocaleLowerCase().startsWith('blob:')) {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Unable to open local media (${response.status})`);
          sourceBlob = await response.blob();
        }
        const decoded = await DecodedSource.create({
          id: url,
          kind: 'video',
          blob: sourceBlob,
          url: sourceBlob ? null : url,
        });
        if (!decoded.hasVideo()) {
          decoded.dispose();
          entry.failed = true;
          return null;
        }
        entry.decoded = decoded;
        return decoded;
      } catch {
        entry.failed = true;
        return null;
      }
    })();

    this.sources.set(url, entry);
    return entry;
  }

  /** Draw a decoded frame once onto a native-size OffscreenCanvas for cheap re-serve. */
  private snapshot(frame: { width: number; height: number; draw: (ctx: OffscreenCanvasRenderingContext2D, dx: number, dy: number, dw: number, dh: number) => void }): CachedSnapshot | null {
    if (typeof OffscreenCanvas === 'undefined') return null;
    const w = Math.max(1, Math.round(frame.width));
    const h = Math.max(1, Math.round(frame.height));
    try {
      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      frame.draw(ctx, 0, 0, w, h);
      return { canvas, width: w, height: h };
    } catch {
      return null;
    }
  }

  private drawableFromSnapshot(snap: CachedSnapshot): PreviewDrawable {
    return {
      width: snap.width,
      height: snap.height,
      draw: (ctx, dx, dy, dw, dh) => ctx.drawImage(snap.canvas, dx, dy, dw, dh),
      close: () => { /* snapshots live in the LRU until evicted */ },
    };
  }

  private drawableFromDecodedVisual(visual: DecodedVisual): PreviewDrawable {
    return {
      width: visual.frame.width,
      height: visual.frame.height,
      draw: (ctx, dx, dy, dw, dh) =>
        visual.frame.draw(ctx as unknown as OffscreenCanvasRenderingContext2D, dx, dy, dw, dh),
      close: () => visual.close(),
    };
  }

  private putLru(lru: Map<number, CachedSnapshot>, key: number, snap: CachedSnapshot): void {
    lru.set(key, snap);
    while (lru.size > LRU_MAX) {
      const oldestKey = lru.keys().next().value;
      if (oldestKey === undefined) break;
      lru.delete(oldestKey);
    }
  }

  private touchLru(lru: Map<number, CachedSnapshot>, key: number, snap: CachedSnapshot): void {
    // Move-to-end so genuinely-recent snapshots survive eviction.
    lru.delete(key);
    lru.set(key, snap);
  }
}
